/**
 * Scorecard traffic lights and alert rules, over stored KPI values. Comparisons
 * are exact; messages use the formula library's own display strings.
 */
import { formatCents } from "../money";
import { Exact } from "./exact";
import { displayKpi, KPI_DEFINITIONS, type KpiKey } from "./kpis";

export type Light = "green" | "amber" | "red" | "none";
export type Target = { comparator: "at_least" | "at_most"; target: string; tolerance: string };

/**
 * Green when the target is met. Amber when missed by no more than tolerance × |target|
 * (default 10%). Red otherwise. "none" when the KPI has no value.
 */
export function lightFor(value: Exact | null, t: Target): Light {
  if (!value) return "none";
  const target = Exact.decimal(t.target);
  const band = target.abs().mul(Exact.decimal(t.tolerance));
  if (t.comparator === "at_least") {
    if (value.cmp(target) >= 0) return "green";
    return value.cmp(target.sub(band)) >= 0 ? "amber" : "red";
  }
  if (value.cmp(target) <= 0) return "green";
  return value.cmp(target.add(band)) <= 0 ? "amber" : "red";
}

/** How a target reads to people: "at least 40.0%", "at most 45.0 days", "at least $10,000.00". */
export function describeTarget(key: KpiKey, t: Target): string {
  return `${t.comparator === "at_least" ? "at least" : "at most"} ${displayKpi(KPI_DEFINITIONS[key].kind, Exact.decimal(t.target))}`;
}

export type StoredValue = { exact: string | null; display: string };
export type AlertDraft = { ruleKey: string; severity: "warning" | "critical"; message: string };

/** Thresholds, written once. */
export const ALERT_RULES = {
  runwayCriticalMonths: Exact.int(3),
  runwayWarningMonths: Exact.int(6),
  dsoIncreaseDays: Exact.int(10),
  grossMarginDropPoints: Exact.frac(4, 100),
  payrollMonthsOfCash: Exact.int(2),
} as const;

const val = (v: StoredValue | undefined) => (v?.exact ? Exact.parse(v.exact) : null);

/**
 * Alerts for one month, given its KPIs, the previous month's KPIs (if any), cash and
 * payroll for the month (cents), and targets. Pure: the caller stores the drafts.
 */
export function alertsFor(input: {
  current: Partial<Record<KpiKey, StoredValue>>;
  previous: Partial<Record<KpiKey, StoredValue>> | null;
  cashCents: number;
  payrollCents: number | null;
  targets: Partial<Record<KpiKey, Target>>;
  preliminary: boolean;
}): AlertDraft[] {
  const out: AlertDraft[] = [];
  const tag = input.preliminary ? " (books not closed yet)" : "";

  if (input.payrollCents !== null && input.payrollCents > 0) {
    const need = Exact.int(input.payrollCents).mul(ALERT_RULES.payrollMonthsOfCash);
    if (Exact.int(input.cashCents).cmp(need) < 0) {
      out.push({ ruleKey: "cash_below_payroll", severity: "critical", message: `Cash (${formatCents(input.cashCents)}) is below two months of payroll (${formatCents(need.toCents())})${tag}.` });
    }
  }

  const runway = val(input.current.runway);
  if (runway) {
    if (runway.cmp(ALERT_RULES.runwayCriticalMonths) < 0) out.push({ ruleKey: "runway_short", severity: "critical", message: `${KPI_DEFINITIONS.runway.label} is ${input.current.runway!.display}${tag}.` });
    else if (runway.cmp(ALERT_RULES.runwayWarningMonths) < 0) out.push({ ruleKey: "runway_short", severity: "warning", message: `${KPI_DEFINITIONS.runway.label} is ${input.current.runway!.display}${tag}.` });
  }

  if (input.previous) {
    const dso = val(input.current.dso);
    const dsoPrev = val(input.previous.dso);
    if (dso && dsoPrev) {
      const up = dso.sub(dsoPrev);
      if (up.cmp(ALERT_RULES.dsoIncreaseDays) >= 0) out.push({ ruleKey: "dso_up", severity: "warning", message: `Customers are paying slower: DSO up ${displayKpi("days", up)} to ${input.current.dso!.display}${tag}.` });
    }
    const gm = val(input.current.gross_margin);
    const gmPrev = val(input.previous.gross_margin);
    if (gm && gmPrev) {
      const down = gmPrev.sub(gm);
      if (down.cmp(ALERT_RULES.grossMarginDropPoints) >= 0) {
        out.push({ ruleKey: "gross_margin_down", severity: "warning", message: `Gross margin fell ${down.mul(Exact.int(100)).toFixed(1)} points to ${input.current.gross_margin!.display}${tag}.` });
      }
    }
  }

  for (const [key, t] of Object.entries(input.targets) as Array<[KpiKey, Target]>) {
    if (lightFor(val(input.current[key]), t) === "red") {
      out.push({ ruleKey: `off_target_${key}`.slice(0, 40), severity: "warning", message: `${KPI_DEFINITIONS[key].label} is ${input.current[key]!.display}, target ${describeTarget(key, t)}${tag}.` });
    }
  }
  return out;
}
